import { FeatureDetector, throttle } from '@eyeson/js';

const supportsScreenOrientation =
  Reflect.has(window, 'screen') && Reflect.has(window.screen, 'orientation');
const isSupported = FeatureDetector.isIOSDevice() && supportsScreenOrientation;

/*
In iOS WebViews / WKWebView issues occur as soon as the device is rotated to
landscape.

1. The camera video is in wrong angle. In real browser the video also changes
  orientation.
2. Rotate to landscape and back will cause a misalignment of displayed buttons 
  and touch area (you need to touch above the button to trigger a click…)

This detector is based on the fact that screen orientation change event does NOT
fire, even though window.resize does fire.
https://developer.mozilla.org/en-US/docs/Web/API/ScreenOrientation/type

Some browsers fire screen.orientation change before and others after 
resize event, so I've added a throttle to make sure it works.
The 400ms is just chosen by experience.
*/

class WebViewDetection {
  constructor() {
    this._isPortrait = false;
    this._handler = null;
    this._hasMismatch = false;
    this._cancel = false;
  }

  init(callback) {
    if (!isSupported) {
      return;
    }
    this._isPortrait = window.innerHeight > window.innerWidth;
    const type = this._isPortrait ? 'portrait' : 'landscape';
    if (!window.screen.orientation.type.startsWith(type)) {
      this._hasMismatch = true;
      callback();
    }
    this.fixWebViewPositioning();
    if (this._handler) {
      window.removeEventListener('resize', this._handler);
    }
    this._handler = throttle(
      () => {
        const isPortraitNow = window.innerHeight > window.innerWidth;
        if (isPortraitNow !== this._isPortrait) {
          this._isPortrait = isPortraitNow;
          if (!this._cancel) {
            const type = this._isPortrait ? 'portrait' : 'landscape';
            if (!window.screen.orientation.type.startsWith(type)) {
              this._hasMismatch = true;
              callback();
            }
          }
          this.fixWebViewPositioning();
        }
      },
      400,
      { leading: false }
    );
    window.addEventListener('resize', this._handler);
  }

  fixWebViewPositioning() {
    // eyeson app has position:fixed on the body and this causes an issue in
    // ios webviews where the hit area doesn't mach the hit element
    // https://stackoverflow.com/questions/28489892/wkwebview-shows-a-cropped-background-and-blank-space-after-rotation-on-ipad
    if (this._hasMismatch) {
      requestAnimationFrame(() => window.scrollBy(0, -1));
    }
  }

  cancel() {
    if (this._hasMismatch) {
      this._cancel = true;
    } else if (this._handler) {
      window.removeEventListener('resize', this._handler);
      this._handler = null;
    }
  }
}

WebViewDetection.isSupported = isSupported;

export default WebViewDetection;
